import React, { useState, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import hexToRgba from "../../../utils/hexToRgba";
import iconClose from "../../../assets/icon-close.svg";

import "./ExpandableList.css";

const ExpandableList = ({ filters, handleFilterChange, isValidateAdvancedSearchAccess }) => {
  const sliderDebounceRef = useRef({});
  const [tempSliderValues, setTempSliderValues] = useState({});
  const [showAllDayToDay, setShowAllDayToDay] = useState(false);

  const handleRatingChange = (newValue, category, id) => {
    // Atualiza o valor temporário do slider imediatamente (visual)
    const itemKey = `${category}-${id}`;
    setTempSliderValues(prev => ({
      ...prev,
      [itemKey]: newValue
    }));

    // Limpa o timeout anterior para este item específico
    if (sliderDebounceRef.current[itemKey]) {
      clearTimeout(sliderDebounceRef.current[itemKey]);
    }

    // Só aplica o filtro após 500ms de inatividade
    sliderDebounceRef.current[itemKey] = setTimeout(() => {
      const updatedSkills = filters.skills.map((group) => {
        if (group[category]) {
          return {
            [category]: group[category].map((skill) =>
              skill.id === id
                ? { ...skill, level: newValue, checked: newValue > 0 }
                : skill
            ),
          };
        }
        return group;
      });

      handleFilterChange("skills", updatedSkills);

      // Remove o valor temporário
      setTempSliderValues(prev => {
        const newValues = { ...prev };
        delete newValues[itemKey];
        return newValues;
      });

      delete sliderDebounceRef.current[itemKey];
    }, 500);
  };

  const handleCheckboxChange = (category, id) => {
    const hasAccess = isValidateAdvancedSearchAccess()?.allowed;

    const updatedSkills = filters.skills.map((group) => {
      if (group[category]) {
        return {
          [category]: group[category].map((skill) => {
            if (skill.id === id) {
              const newChecked = !skill.checked;
              const isPlanilha = isSpreadsheet(skill.name);
              // Se o usuário NÃO tem acesso avançado e está MARCANDO o checkbox E é Planilhas, define level como 1
              const newLevel = !hasAccess && newChecked && isPlanilha ? 1 : (newChecked ? skill.level : 0);
              return { ...skill, checked: newChecked, level: newLevel };
            }
            return skill;
          }),
        };
      }
      return group;
    });

    handleFilterChange("skills", updatedSkills);
  };

  const handleRemoveSkill = (category, id) => {
    const updatedSkills = filters.skills.map((group) => {
      if (group[category]) {
        return {
          [category]: group[category].map((skill) =>
            skill.id === id ? { ...skill, checked: false, level: 0 } : skill
          ),
        };
      }
      return group;
    });

    handleFilterChange("skills", updatedSkills);
  };

  const isSpreadsheet = (itemName) => {
    return itemName?.toLowerCase().includes('planilha');
  };

  const sliderHandleColor = (value) => {
    switch (value) {
      case 0:
        return "black";
      case 1:
        return "#EC5569";
      case 2:
        return "#ECA355";
      case 3:
        return "#009ABC";
      case 4:
        return "#57BAB8";
      default:
        return null;
    }
  };

  const handleValueLabelFormat = (value) => {
    switch (value) {
      case 0:
        return "Não conheço";
      case 1:
        return "Sei o básico";
      case 2:
        return "Estou aprendendo";
      case 3:
        return "Me viro bem";
      case 4:
        return "Sou expert";
      default:
        return null;
    }
  };

  const labelCase = {
    operation: "Operação",
    marketing: "Marketing e Vendas",
    marketplace: "Marketplaces",
    platform: "Plataformas",
    erp: "ERPs",
    designAndMedia: "Design e Mídia",
    dataAndBi: "Dados & BI",
    socialMedia: "Social Media",
    ia: "Inteligência artificial",
    dayToDay: "Produtividade e Gestão",
  };

  const [visibleCount, setVisibleCount] = useState(3); // Começa mostrando 5 itens
  const [showAll, setShowAll] = useState(false);

  const visibleItems = showAll
    ? filters.skills
    : filters.skills?.slice(0, visibleCount);

  const handleShowMore = () => {
    setVisibleCount(visibleCount + 3); // Adiciona mais 5 itens a cada clique
    if (visibleCount >= filters.skills?.length) {
      setShowAll(true); // Se todos os itens estiverem visíveis, muda para o modo "Ver Menos"
    }
  };

  const handleShowLess = () => {
    setVisibleCount(3); // Volta a mostrar apenas os 5 primeiros
    setShowAll(false);
  };

  return (
    <div className="containerCardsSLides">
      {filters?.skills
        ?.slice()
        .sort((a, b) => {
          // Coloca "DIA A DIA" (dayToDay) sempre primeiro
          const categoryA = Object.keys(a)[0];
          const categoryB = Object.keys(b)[0];

          if (categoryA === 'dayToDay') return -1;
          if (categoryB === 'dayToDay') return 1;

          return 0; // Mantém a ordem original para os outros
        })
        .map((group, index) => {
        const category = Object.keys(group)[0]; // Obtém o nome da categoria (marketing, projects, etc.)
        const skills = group[category]; // Obtém os itens da categoria

        const hasCheckedItem = skills.some((item) => item.checked);

        const hasAdvancedAccess = isValidateAdvancedSearchAccess()?.allowed;

        return (
          <Accordion
            key={index}
            defaultExpanded={hasCheckedItem || (category === 'dayToDay' && !hasAdvancedAccess)}
            sx={{
              border: "none",
              boxShadow: "none",
              backgroundColor: "transparent",
              "&:before": {
                display: "none",
              },
              borderBottom: "none",
            }}
            disableGutters
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ margin: "0px", padding: "0px" }}
            >
              <div className="textSLideHeader">{labelCase[category]}</div>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                margin: "0px",
                padding: "0px",
                gap: "10px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {skills
                ?.slice()
                .filter((item) => {
                  // Se for categoria dayToDay e NÃO tiver acesso avançado e não mostrar tudo
                  if (category === 'dayToDay' && !showAllDayToDay) {
                    const hasAccess = isValidateAdvancedSearchAccess()?.allowed;

                    if (!hasAccess) {
                      return isSpreadsheet(item.name); // Mostra apenas planilhas
                    }
                  }
                  return true; // Mostra todos os outros
                })
                .sort((a, b) => {
                  // Se for categoria dayToDay, colocar "Planilha" primeiro
                  if (category === 'dayToDay') {
                    if (a.name.toLowerCase().includes('planilha')) return -1;
                    if (b.name.toLowerCase().includes('planilha')) return 1;
                  }
                  return a.name.localeCompare(b.name);
                })
                .map((item) => {
                  const isPlanilha = isSpreadsheet(item.name);
                  const hasAccess = isValidateAdvancedSearchAccess()?.allowed;
                  const isDisabled = !isPlanilha && !hasAccess;

                  // Usa valor temporário se existir, senão usa o valor do item
                  const itemKey = `${category}-${item.id}`;
                  const currentLevel = tempSliderValues[itemKey] ?? item.level;

                  return (
                    <div
                      key={item.id}
                      className={`card ${item.checked && "checked"}`}
                      style={{ position: 'relative' }}
                    >
                      {/* Botão X no canto superior direito (apenas usuários com acesso) */}
                      {hasAccess && item.checked && (
                        <button
                          className="remove-filter-btn"
                          onClick={() => handleRemoveSkill(category, item.id)}
                          style={{
                            position: 'absolute',
                            top: '6px',
                            right: '6px',
                            background: 'rgba(255, 255, 255, 0.8)',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 5,
                            borderRadius: '50%',
                            transition: 'all 0.2s ease-in-out',
                            opacity: 0.7
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.transform = 'scale(1.1)';
                            e.currentTarget.style.background = 'rgba(236, 85, 105, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '0.7';
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                          }}
                        >
                          <img src={iconClose} alt="remover" style={{ width: '10px', height: '10px' }} />
                        </button>
                      )}

                      {/* Checkbox (apenas usuários sem acesso) */}
                      {!hasAccess && (
                        <label className="check-label">
                          <input
                            type="checkbox"
                            checked={item.checked}
                            onChange={() => handleCheckboxChange(category, item.id)}
                          />
                        </label>
                      )}

                      <div className="conatinerLogoFilters">
                        <img src={item?.logo} alt="" style={{ height: "24px" }} />
                      </div>

                      <div className="conteinerNivel" style={{ width: "100%" }}>
                        <div className="text">{item.name}</div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            opacity: isDisabled ? 0.4 : 1,
                            pointerEvents: isDisabled ? 'none' : 'auto',
                          }}
                        >
                          <Slider
                            style={{
                              margin: "0px",
                              width: "100%",
                              padding: "0px",
                            }}
                            sx={{
                              "& .MuiSlider-thumb:hover": {
                                boxShadow: `0px 0px 0px 8px ${hexToRgba(
                                  sliderHandleColor(currentLevel),
                                  0.16
                                )}`,
                              },
                              "& .MuiSlider-thumb.Mui-active": {
                                boxShadow: `0px 0px 0px 14px ${hexToRgba(
                                  sliderHandleColor(currentLevel),
                                  0.16
                                )}`,
                              },
                              "& .MuiSlider-thumb": {
                                width: 20, // Adjust thumb size
                                height: 20, // Adjust thumb size
                                marginLeft: "-8px",
                                zIndex: 1000, // Thumb acima de tudo
                              },
                              "& .MuiSlider-track": {
                                height: 10, // Ensure track height matches your design
                              },
                              "& .MuiSlider-valueLabel": {
                                zIndex: 9999, // Tooltip muito acima do botão X
                              },
                              "& .MuiSlider-valueLabelOpen": {
                                zIndex: 9999,
                              },
                            }}
                            slotProps={{
                              track: {
                                style: {
                                  backgroundColor: sliderHandleColor(currentLevel),
                                  // borderColor: sliderHandleColor(currentLevel),
                                  border: "0.95px solid #ABABAB",
                                  height: "10px",
                                },
                              },
                              thumb: {
                                style: {
                                  backgroundColor: "#575757",
                                  marginLeft:
                                    currentLevel === 0 || currentLevel === undefined
                                      ? "8px"
                                      : null,
                                  border: "0.95px solid #ffff",
                                  ":hover": {
                                    color: sliderHandleColor(currentLevel),
                                  },
                                },
                              },
                              rail: {
                                style: {
                                  backgroundColor: "#FFFFFF",
                                  height: "10px",
                                  border: "0.95px solid #ABABAB",
                                  width: "100%",
                                },
                              },
                            }}
                            min={0}
                            defaultValue={0}
                            value={currentLevel}
                            onChange={(e) => {
                              handleRatingChange(
                                e.target.value,
                                category,
                                item.id
                              );
                            }}
                            max={4}
                            valueLabelFormat={handleValueLabelFormat}
                            valueLabelDisplay="auto"
                            disabled={isDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

              {/* Botão "Ver mais/menos" para DIA A DIA quando não tem acesso avançado */}
              {category === 'dayToDay' && (() => {
                const hasAccess = isValidateAdvancedSearchAccess()?.allowed;

                if (!hasAccess) {
                  return (
                    <button 
                      className="load-more-button" 
                      onClick={() => setShowAllDayToDay(!showAllDayToDay)}
                      style={{ 
                        marginTop: '10px',
                      }}
                    >
                      {showAllDayToDay ? 'Ver menos -' : 'Ver mais +'}
                    </button>
                  );
                }
              })()}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* {filters?.knowledge?.length > visibleCount && !showAll && (
        <button className="load-more-button" onClick={handleShowMore}>
          Ver Mais
        </button>
      )}

      {showAll && (
        <button className="load-more-button" onClick={handleShowLess}>
          Ver Menos
        </button>
      )} */}
    </div>
  );
};

export default ExpandableList;
